<template>
  <div class="space-y-3">
    <div v-if="!groups.length" class="text-center text-gray-400 text-sm py-16 bg-white border border-gray-300 rounded-xl">
      No curriculum found
    </div>

    <div
      v-for="prog in groups"
      :key="prog.programme"
      class="bg-white border border-gray-300 rounded-xl shadow-sm overflow-hidden"
    >
      <!-- Programme header -->
      <button
        type="button"
        class="w-full flex items-center justify-between gap-3 px-5 py-3.5 bg-gray-50 hover:bg-gray-100 transition-colors"
        @click="toggle(prog.programme)"
      >
        <div class="flex items-center gap-2 min-w-0">
          <i
            class="pi pi-chevron-right text-gray-400 text-sm transition-transform"
            :class="{ 'rotate-90': isOpen(prog.programme) }"
          />
          <span class="font-semibold text-gray-800 truncate">{{ prog.programme }}</span>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <Tag :value="`${prog.entryCount} ${prog.entryCount === 1 ? 'entry' : 'entries'}`" severity="secondary" rounded />
          <Tag :value="`${prog.totalUnits} units`" rounded />
        </div>
      </button>

      <!-- Levels -->
      <div v-if="isOpen(prog.programme)" class="divide-y divide-gray-100">
        <div v-for="level in prog.levels" :key="level.level" class="px-5 py-3">
          <div class="text-xs font-bold uppercase tracking-wide text-primary mb-2">Level {{ level.level }}</div>

          <!-- Semesters -->
          <div v-for="sem in level.semesters" :key="sem.semester" class="mb-3 last:mb-0">
            <div class="flex items-center gap-2 mb-1.5">
              <span class="text-xs font-semibold text-gray-600">{{ sem.semester }}</span>
              <span class="text-[11px] text-gray-400">· {{ sem.entries.length }} {{ sem.entries.length === 1 ? 'course' : 'courses' }} · {{ sem.units }} units</span>
            </div>

            <!-- Entries -->
            <div class="space-y-1">
              <div
                v-for="entry in sem.entries"
                :key="entry.row.id"
                class="flex items-center gap-3 rounded-lg border border-gray-100 hover:border-primary/30 hover:bg-primary/5 px-3 py-2 transition-colors"
              >
                <div class="flex-1 min-w-0 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span v-for="(c, ci) in entry.courses" :key="ci" class="inline-flex items-center gap-1.5 text-sm">
                    <span class="font-mono text-xs font-semibold text-primary">{{ c.code }}</span>
                    <span class="text-gray-700">{{ c.title }}</span>
                    <span class="text-[11px] text-gray-400">{{ c.unit }}u</span>
                  </span>
                </div>
                <Tag :value="entry.status" :severity="entry.status === 'Compulsory' ? undefined : 'secondary'" rounded />
                <div v-if="!readonly" class="flex gap-1 shrink-0">
                  <Button icon="pi pi-pencil" class="p-button-text p-button-rounded p-button-sm" v-tooltip.top="'Edit'" @click="$emit('edit', entry.row)" />
                  <Button icon="pi pi-trash" class="p-button-danger p-button-text p-button-rounded p-button-sm" v-tooltip.top="'Delete'" @click="$emit('delete', entry.row)" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CurriculumRow, ProgrammeGroup } from './curriculumGrouping';
import { groupCurriculumRows } from './curriculumGrouping';

// readonly: rendered as a viewer (e.g. the AI workspace curriculum card) —
// the per-entry edit/delete buttons are hidden.
const props = defineProps<{ rows: CurriculumRow[]; readonly?: boolean }>();
defineEmits<{ edit: [row: CurriculumRow]; delete: [row: CurriculumRow] }>();

const groups = computed<ProgrammeGroup[]>(() => groupCurriculumRows(props.rows));

const collapsed = ref<Set<string>>(new Set());
const isOpen = (programme: string) => !collapsed.value.has(programme);
const toggle = (programme: string) => {
  const next = new Set(collapsed.value);
  if (next.has(programme)) next.delete(programme);
  else next.add(programme);
  collapsed.value = next;
};
</script>

<style scoped>
.rotate-90 {
  transform: rotate(90deg);
}
</style>
